import { ReactComponent as OutlineChevronLeft } from "assets/svg/outline-chevron-left-16.svg";
import { ReactComponent as OutlinePlus } from "assets/svg/outline-plus.svg";
import { useSchedule } from "components/Dashboard/DoctorDashboard/contexts/ScheduleContext";
import { useCalendarTime } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/CalendarTimeContext";
import ErrorMessage from "components/error-message";
import IconButton from "components/icon-button";
import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import postSchedule from "services/scheduler/postSchedule";
import { hideMobileSidebar } from "store/calendarSidebarSlice";
import { PRO_CALENDAR_ANALYTICS_PAGE_NAME, track } from "utils/amplitude";
import Schedule from "../../../calendar-view/components/available-time-periods-viewer/components/schedule/Schedule";
import styles from "./AvailabilityMobileScreen.module.scss";
import AvailabilitySchedule from "./AvailabilitySchedule";

const ANALYTICS_COMPONENT_NAME = "edit-availability-mobile-screen";

const AvailabilityMobileScreen = () => {
  const dispatch = useDispatch();

  const [error, setError] = useState<any>();

  const { currentCalendarTimezone } = useCalendarTime();
  const { schedule, setSchedule } = useSchedule();

  const [isEditing, setIsEditing] = useState(false);
  const [selectedPeriodIndex, setSelectedPeriodIndex] = useState(-1);

  const trackClick = useCallback(
    (element: string) =>
      track(
        PRO_CALENDAR_ANALYTICS_PAGE_NAME,
        [ANALYTICS_COMPONENT_NAME, element],
        "click",
      ),
    [],
  );

  const saveScheduleAndUpdateLocalValue = useCallback(
    async (scheduleToPost: AvailableTimePeriod[]) => {
      try {
        const updatedSchedule = await postSchedule(scheduleToPost);
        setSchedule(updatedSchedule);
      } catch (error) {
        setError(error);
      }
    },
    [setSchedule],
  );

  const duplicateSchedule = useCallback(
    (i) =>
      setSchedule((prevSchedule) => {
        const nextSchedule = [
          ...prevSchedule.slice(0, i),
          { ...prevSchedule[i] },
          ...prevSchedule.slice(i),
        ];
        saveScheduleAndUpdateLocalValue(nextSchedule);
        return nextSchedule;
      }),
    [saveScheduleAndUpdateLocalValue, setSchedule],
  );

  const deleteSchedule = useCallback(
    (i) =>
      setSchedule((prevSchedule) => {
        const nextSchedule = [
          ...prevSchedule.slice(0, i),
          ...prevSchedule.slice(i + 1),
        ];
        saveScheduleAndUpdateLocalValue(nextSchedule);
        return nextSchedule;
      }),
    [saveScheduleAndUpdateLocalValue, setSchedule],
  );

  const replaceSchedule = useCallback(
    (i, newSchedule: AvailableTimePeriod) =>
      setSchedule((prevSchedule) => {
        const nextSchedule = [
          ...prevSchedule.slice(0, i),
          newSchedule,
          ...prevSchedule.slice(i + 1),
        ];
        saveScheduleAndUpdateLocalValue(nextSchedule);
        return nextSchedule;
      }),
    [saveScheduleAndUpdateLocalValue, setSchedule],
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <IconButton onClick={() => dispatch(hideMobileSidebar())}>
          <OutlineChevronLeft />
        </IconButton>
        My Availability
        <div style={{ visibility: "hidden" }}>
          <IconButton>
            <OutlineChevronLeft />
          </IconButton>
        </div>
      </div>
      {error && (
        <ErrorMessage
          show={true}
          onHide={() => setError(undefined)}
          errorMsg={error.toString()}
        />
      )}
      <div className={styles.content}>
        {schedule.map((timePeriod, i) => (
          <Schedule
            className={styles["available-time-periods-viewer__list-item"]}
            active={false}
            key={i} // this is wrong but time periods have no unique ids
            order={i + 1}
            timezone={currentCalendarTimezone}
            value={timePeriod}
            invalid={false}
            onClick={() => {
              trackClick("schedule");
              setSelectedPeriodIndex(i);
              setIsEditing(true);
            }}
            onCopy={() => {
              trackClick("duplicate-schedule-dropdown-option");
              duplicateSchedule(i);
            }}
            onDelete={() => {
              trackClick("delete-schedule-dropdown-option");
              deleteSchedule(i);
            }}
          />
        ))}
      </div>
      <button
        className={styles["add-button"]}
        onClick={() => {
          setSelectedPeriodIndex(-1);
          setIsEditing(true);
        }}
      >
        <OutlinePlus color="white" />
      </button>

      {isEditing && (
        <div className={styles["editor-backdrop"]}>
          <AvailabilitySchedule
            schedule={schedule[selectedPeriodIndex]}
            close={() => {
              setIsEditing(false);
              setSelectedPeriodIndex(-1);
            }}
            save={(updatedPeriod) => {
              replaceSchedule(selectedPeriodIndex, updatedPeriod);
              setIsEditing(false);
              setSelectedPeriodIndex(-1);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default AvailabilityMobileScreen;
